import { effect, inject, Injectable, Injector, untracked } from '@angular/core';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { angularPlugin } from '@datadog/browser-rum-angular';
import { ApplicationUser } from '@app/shared/models/classes/ApplicationUser';
import { environment } from '@env/environment';

import { version as appVersion } from '../../../package.json';
import { AuthenticationService } from './authentication/authentication.service';

/** Identity attached to every RUM event and browser log of a session. */
export interface DatadogUser {
  id: string;
  email: string;
  name: string;
  /** Datadog's `User` type carries an open index signature; mirror it so the object is assignable. */
  [key: string]: unknown;
}

export function toDatadogUser(user: ApplicationUser): DatadogUser {
  const name = [user.firstname, user.lastname].filter((part) => !!part).join(' ');
  return {
    id: user.id,
    email: user.email,
    name: name || user.email,
  };
}

/**
 * Initializes Datadog Real User Monitoring (views, clicks, resources, errors,
 * session replay) and keeps the signed-in administrator attached to RUM and to
 * the browser logs, so a session in Datadog can be searched by `@usr.email`.
 *
 * Active only when `environment.datadog.clientToken` and `rumApplicationId` are
 * set; local DEV stays inactive. The user follows the authentication state:
 * set on login / session restore, cleared on logout.
 */
@Injectable({ providedIn: 'root' })
export class DatadogRumService {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly injector = inject(Injector);
  private initialized = false;

  init(): void {
    if (this.initialized) {
      return;
    }

    const config = environment.datadog;
    if (!config?.clientToken || !config.rumApplicationId) {
      return;
    }

    try {
      datadogRum.init({
        applicationId: config.rumApplicationId,
        clientToken: config.clientToken,
        site: config.site,
        service: config.service,
        env: config.env,
        version: appVersion,
        sessionSampleRate: 100,
        // The master admin is an internal tool with a handful of sessions per
        // day; recording all of them is what makes "what did X do" answerable.
        sessionReplaySampleRate: 100,
        trackResources: true,
        trackLongTasks: true,
        trackUserInteractions: true,
        defaultPrivacyLevel: 'mask-user-input',
        plugins: [angularPlugin({ router: true })],
      });

      this.trackAuthenticatedUser();
      this.initialized = true;
    } catch (error) {
      // Never let a monitoring-init failure break app bootstrap.
      // eslint-disable-next-line no-console
      console.error('[DatadogRumService] init failed', error);
    }
  }

  private trackAuthenticatedUser(): void {
    effect(
      () => {
        const user = this.authenticationService.credentialsSignal()?.user ?? null;
        untracked(() => applyUser(user));
      },
      { injector: this.injector },
    );
  }
}

function applyUser(user: ApplicationUser | null): void {
  if (user) {
    const datadogUser = toDatadogUser(user);
    datadogRum.setUser(datadogUser);
    datadogLogs.setUser(datadogUser);
  } else {
    datadogRum.clearUser();
    datadogLogs.clearUser();
  }
}
